import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { ArrowRight } from "lucide-react";
import { BrandMarkDark } from "../components/BrandMark";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
    const [err, setErr] = useState("");
    const [pending, setPending] = useState(null);
    const [resent, setResent] = useState(false);
    const resendInvite = async () => {
        try { await api.post("/auth/invite/resend-to-me", { email: form.email }); setResent(true); }
        catch { setErr("Odeslání pozvánky selhalo, zkuste to později."); }
    };
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await register(form);
            navigate("/");
        } catch (ex) {
            const d = ex.response?.data?.detail;
            if (ex.response?.status === 409 && d?.code === "pending_invite") { setPending(d); setErr(""); }
            else setErr(formatApiError(d) || "Registrace selhala");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <div className="w-full max-w-md bg-white border border-slate-200 sharp-shadow p-8">
                <div className="mb-8">
                    <BrandMarkDark />
                </div>

                <div className="label-eyebrow mb-2">Registrace</div>
                <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mb-6">Vytvořte si účet.</h1>

                <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                    <Field label="Jméno a příjmení" v={form.name} on={set("name")} testid="reg-name" required />
                    <Field label="Název firmy" v={form.company_name} on={set("company_name")} testid="reg-company" />
                    <Field label="Email" type="email" v={form.email} on={set("email")} testid="reg-email" required />
                    <Field label="Heslo (min. 6 znaků)" type="password" v={form.password} on={set("password")} testid="reg-password" required />

                    {pending && (
                        <div className="border-l-4 border-orange-600 bg-orange-50 px-4 py-3 text-sm text-slate-800 space-y-2" data-testid="register-pending-invite">
                            <div>Pro e-mail <b>{form.email}</b> už existuje čekající pozvánka do firmy <b>{pending.company_name}</b> ({pending.role === "admin" ? "administrátor" : "vedoucí technik"}). Místo zakládání nové firmy ji přijměte.</div>
                            {resent ? <div className="text-emerald-700 font-semibold" data-testid="register-invite-resent">Pozvánku jsme znovu poslali na váš e-mail – otevřete odkaz „Přijmout pozvánku“.</div>
                                : <button type="button" onClick={resendInvite} data-testid="register-resend-invite" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-slate-900 text-white rounded-sm">Poslat mi pozvánku znovu</button>}
                        </div>
                    )}
                    {err && (
                        <div className="border-l-4 border-red-600 bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="register-error">{err}</div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="register-submit"
                        className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm transition-colors disabled:opacity-50 rounded-sm"
                    >
                        {loading ? "Vytvářím účet…" : "Vytvořit účet"}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 text-center">
                    Máte už účet?{" "}
                    <Link to="/prihlaseni" data-testid="link-login" className="font-semibold text-orange-600 hover:underline">
                        Přihlásit se
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", testid, required }) {
    return (
        <div>
            <label className="label-eyebrow block mb-2">{label}</label>
            <input
                type={type}
                value={v}
                onChange={on}
                required={required}
                data-testid={testid}
                className="w-full px-4 py-3 border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm"
            />
        </div>
    );
}
